import type { CreatePaymentFlowInput } from '@/lib/api/paymentFlows';
import {
  SETUP_PAYMENT_PROCESSOR_TYPES,
  type SetupPaymentProcessorType,
} from '@/components/setup/experience/constants';
import type { SetupActivePayment } from '@/components/setup/experience/lib/setupPaymentSummary';
import type { CreateProcessorInput, Processor, TagadapayAccount } from '@/lib/api/processors';

export type SetupLivePayment =
  | { kind: 'existing'; processorId: string }
  | { kind: 'create'; tagadapayAccountId: string }
  | { kind: 'unavailable' };

type SetupProcessor = Pick<Processor, 'id' | 'type' | 'enabled' | 'archivedAt'> & { options: unknown };
export type SetupTagadapayAccount = Pick<TagadapayAccount, 'id' | 'status'>;

function getProcessorOption(options: unknown, key: string): unknown {
  return options && typeof options === 'object' && !Array.isArray(options) && key in options
    ? Reflect.get(options, key)
    : undefined;
}

function getSetupActiveTagadapayAccounts(
  tagadapayAccounts: readonly SetupTagadapayAccount[],
): readonly SetupTagadapayAccount[] {
  return tagadapayAccounts.filter((account) => account.status === 'active');
}

/** Accounts arrive newest first; keep an already-bound, payable router ahead of that order. */
export function resolveSetupLivePayment(
  processors: readonly SetupProcessor[],
  tagadapayAccounts: readonly SetupTagadapayAccount[],
  activeProcessorId: string | null,
): SetupLivePayment {
  const activeAccounts = getSetupActiveTagadapayAccounts(tagadapayAccounts);
  if (activeAccounts.length === 0) return { kind: 'unavailable' };

  const routers = processors.filter(
    (processor) =>
      processor.type === 'tagadapay-router' &&
      processor.enabled &&
      !processor.archivedAt &&
      activeAccounts.some(
        (account) => account.id === getProcessorOption(processor.options, 'tagadapayAccountId'),
      ),
  );
  const bound = routers.find((processor) => processor.id === activeProcessorId);
  if (bound) return { kind: 'existing', processorId: bound.id };

  for (const account of activeAccounts) {
    const router = routers.find(
      (processor) => getProcessorOption(processor.options, 'tagadapayAccountId') === account.id,
    );
    if (router) return { kind: 'existing', processorId: router.id };
  }

  return { kind: 'create', tagadapayAccountId: activeAccounts[0].id };
}

export function isSetupConnectedProcessor(
  processor: SetupProcessor,
  liveProcessorId: string | null,
  tagadapayAccounts: readonly SetupTagadapayAccount[],
): boolean {
  const accountId = getProcessorOption(processor.options, 'accountId');
  const tagadapayAccountId = getProcessorOption(processor.options, 'tagadapayAccountId');
  const hasActiveTagadapayAccount =
    typeof tagadapayAccountId === 'string' &&
    getSetupActiveTagadapayAccounts(tagadapayAccounts).some((account) => account.id === tagadapayAccountId);
  return Boolean(
    processor.enabled &&
    !processor.archivedAt &&
    processor.type !== 'sandbox' &&
    processor.id !== liveProcessorId &&
    (processor.type !== 'tagadapay-router' || hasActiveTagadapayAccount) &&
    (processor.type !== 'tagadapay' || (typeof accountId === 'string' && accountId.startsWith('acct_'))),
  );
}

export function isSetupCustomProcessorAvailable(
  processor: SetupProcessor | undefined,
  tagadapayAccounts: readonly SetupTagadapayAccount[],
): boolean {
  return Boolean(
    processor &&
    (isSetupConnectedProcessor(processor, null, tagadapayAccounts) ||
      (processor.type === 'sandbox' && processor.enabled && !processor.archivedAt)),
  );
}

export function getSetupBoundPaymentSelection(
  active: SetupActivePayment | null,
  livePayment: SetupLivePayment,
  processors: readonly SetupProcessor[],
  tagadapayAccounts: readonly SetupTagadapayAccount[],
): SetupPaymentSelection | null {
  if (!active) return null;
  if (active.processorType === 'sandbox') return { kind: 'quick', type: 'sandbox' };
  if (livePayment.kind === 'existing' && active.processorId === livePayment.processorId) {
    return { kind: 'quick', type: 'tagadapay-router' };
  }
  const processor = processors.find((entry) => entry.id === active.processorId);
  // Offer a visible replacement for an unavailable solo binding; preserve multi-processor flows.
  if (active.isSoloFlow && !isSetupCustomProcessorAvailable(processor, tagadapayAccounts)) return null;
  return { kind: 'custom', processorId: active.processorId, processorType: active.processorType };
}

/** What the merchant picked on the payment step — committed in one go on Continue. */
export type SetupPaymentSelection =
  | { kind: 'quick'; type: SetupPaymentProcessorType }
  | { kind: 'custom'; processorId: string; processorType: string };

export interface SetupCommitPaymentInput {
  storeId: string;
  storeName: string;
  baseCurrency: string;
  selection: SetupPaymentSelection;
  /** Payment currently bound to the store (null when none) — lets the commit reuse/update instead of duplicating. */
  active: SetupActivePayment | null;
}

export function isQuickPaymentType(type: string): type is SetupPaymentProcessorType {
  return SETUP_PAYMENT_PROCESSOR_TYPES.includes(type as SetupPaymentProcessorType);
}

export function isSelectionAlreadyActive(
  selection: SetupPaymentSelection,
  active: SetupActivePayment,
  livePayment: SetupLivePayment = { kind: 'unavailable' },
): boolean {
  return selection.kind === 'custom'
    ? active.processorId === selection.processorId
    : selection.type === 'sandbox'
      ? active.processorType === 'sandbox'
      : livePayment.kind === 'existing' && active.processorId === livePayment.processorId;
}

export function isSetupProviderType(type: string): boolean {
  // Self-serve merchants never receive legacy account ids; routers require the Live card's active TPA.
  return type !== 'tagadapay' && type !== 'tagadapay-router';
}

export function buildSetupProcessorCreateInputForType(type: string): CreateProcessorInput {
  if (!isSetupProviderType(type)) throw new Error('Choose TagadaPay through Live payments');
  return {
    processor: {
      name: 'New processor',
      type,
      enabled: true,
    },
  };
}

export function buildSetupPaymentFlowInput(storeName: string, processorId: string): CreatePaymentFlowInput {
  return {
    data: {
      name: `${storeName} Checkout`,
      strategy: 'simple',
      fallbackMode: false,
      maxFallbackRetries: 0,
      threeDsEnabled: false,
      stickyProcessorEnabled: false,
      pickProcessorStrategy: 'weighted',
      processorConfigs: [
        {
          processorId,
          weight: 100,
          disabled: false,
          nonStickable: false,
        },
      ],
      fallbackProcessorConfigs: [],
      abuseDetectionConfig: null,
    },
  };
}

export function buildSetupProcessorCreateInput(
  processorType: SetupPaymentProcessorType,
  storeName: string,
  livePayment: SetupLivePayment = { kind: 'unavailable' },
): CreateProcessorInput {
  const name = processorType === 'sandbox' ? `${storeName} Sandbox` : `${storeName} TagadaPay`;

  if (processorType === 'sandbox') {
    return {
      processor: {
        name,
        type: 'sandbox' as const,
        enabled: true,
        options: { testMode: true },
      },
    };
  }

  if (livePayment.kind !== 'create') throw new Error('No active TagadaPay account to create a processor');
  return {
    processor: {
      name,
      type: 'tagadapay-router',
      enabled: true,
      options: { tagadapayAccountId: livePayment.tagadapayAccountId },
    },
  };
}
